import { TeamMember } from '../team/team-member.model';

export type TaskSource = 'native' | 'clickup';
export type TaskView = 'board' | 'list' | 'calendar';
export type DetailSection =
  | 'overview'
  | 'description'
  | 'comments'
  | 'checklist'
  | 'attachments'
  | 'clickup'
  | 'activity'
  | 'notification';

export interface ICardData {
  topic: string;
  title: string;
  description: string;
  taskId: string;
  status?: string;
  timeStamp: string | null;
  dueAt: string | null;
  priority: string;
  source?: TaskSource;
  documentsCount: number;
  commentsCount: number;
  id: string;
  assignee: TeamMember | null;
  assignees?: TeamMember[];
  ui: TaskCardUi;
}

export interface ITaskData {
  status: string;
  tasks: ICardData[];
  ui: TaskStatusUi;
}

export interface TaskStatusUi {
  label: string;
  color: string;
  softColor: string;
  slug: string;
}

export interface TaskCardUi {
  status: string;
  statusLabel: string;
  statusColor: string;
  statusSoftColor: string;
  statusSlug: string;
  dueLabel: string;
  isDueSoon: boolean;
  isCompleted: boolean;
  priorityClass: string;
  priorityLabel: string;
  assignees: TeamMember[];
  visibleAssignees: TeamMember[];
  extraAssigneeCount: number;
  assigneeTooltip: string;
}

export interface ITaskApi {
  id: string;
  name: string;
  description?: string | null;
  status: string;
  dueAt: string | null;
  dueLabel: string | null;
  topicId: string;
  priority: string;
  source?: TaskSource;
  documentsCount: number;
  commentsCount: number;
  assignee?: TeamMember | null;
  assignees?: TeamMember[];
}

export interface ITopicListItem {
  id: string;
  name: string;
  uncompletedTaskCount: number;
}

export interface ITaskActivity {
  id: string;
  type?: string | null;
  message: string;
  metadata?: unknown;
  createdAt: string;
  user?: TeamMember | null;
}

export interface ITaskComment {
  id: string;
  body: string;
  commentText?: string | null;
  author: TeamMember | null;
  externalSource?: string | null;
  externalId?: string | null;
  externalSyncStatus?: string | null;
  externalSyncError?: string | null;
  sourceUpdatedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ITaskChecklistItem {
  id?: string;
  title?: string | null;
  name?: string | null;
  text?: string | null;
  label?: string | null;
  completed?: boolean | null;
  done?: boolean | null;
  status?: string | null;
  orderIndex?: number | null;
  clientId?: string;
}

export interface ITaskDocument {
  id: string;
  title: string;
  category?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  currentVersion?: {
    id?: string;
    originalName?: string | null;
    downloadUrl?: string | null;
    mimeType?: string | null;
    sizeBytes?: number | null;
    createdAt?: string | null;
  } | null;
}

export interface IClickUpTaskData {
  mappingId?: string | null;
  taskId?: string | null;
  url?: string | null;
  syncStatus?: string | null;
  syncError?: string | null;
  checksum?: string | null;
  sourceUpdatedAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  pending?: boolean;
}

export interface ITaskDetailData {
  title: string;
  description: string;
  status: string;
  priority: string;
  source?: TaskSource;
  externalSource?: string | null;
  externalId?: string | null;
  externalSyncStatus?: string | null;
  externalSyncError?: string | null;
  externalUrl?: string | null;
  clickUp?: IClickUpTaskData | null;
  dueAt: string | null;
  dueLabel: string | null;
  company: { id: string; name: string; code?: string | null } | null;
  assignees: TeamMember[];
  folderId: string | null;
  notifyInApp: boolean;
  notifyEmail: boolean;
  createdAt: string | null;
  updatedAt: string | null;
  completedAt: string | null;
  documents: ITaskDocument[];
  documentsCount: number;
  comments: ITaskComment[];
  commentsCount: number;
  checklistItems?: ITaskChecklistItem[];
  activity: ITaskActivity[];
}

export const TASK_STATUS_ORDER = [
  'To do',
  'In progress',
  'Blocked',
  'In review',
  'Completed',
] as const;

export const TASK_ASSIGNEE_PREVIEW_LIMIT = 3;

export const DEFAULT_DETAIL_SECTIONS: Record<DetailSection, boolean> = {
  overview: true,
  description: false,
  comments: false,
  checklist: false,
  attachments: false,
  clickup: false,
  activity: false,
  notification: false,
};

const TASK_STATUS_META: Record<
  string,
  { label: string; color: string; softColor: string }
> = {
  'to do': { label: 'To Do', color: '#6B7785', softColor: '#EDF1F3' },
  'in progress': { label: 'In Progress', color: '#F9A60B', softColor: '#FFF4D9' },
  blocked: { label: 'Blocked', color: '#EF4B4B', softColor: '#FFE8E8' },
  'in review': { label: 'In Review', color: '#8B5CF6', softColor: '#F0E9FF' },
  completed: { label: 'Completed', color: '#2CB989', softColor: '#E7F8F1' },
};

export function normalizeTaskStatus(status: string | null | undefined): string {
  return (status || '').trim().toLowerCase();
}

export function displayTaskStatus(status: string | null | undefined): string {
  const normalizedStatus = normalizeTaskStatus(status);
  const statusMeta = TASK_STATUS_META[normalizedStatus];
  if (statusMeta) {
    return statusMeta.label;
  }
  return normalizedStatus
    .split(/\s+/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function taskStatusColor(status: string | null | undefined): string {
  return TASK_STATUS_META[normalizeTaskStatus(status)]?.color ?? '#6B7785';
}

export function taskStatusSoftColor(status: string | null | undefined): string {
  return TASK_STATUS_META[normalizeTaskStatus(status)]?.softColor ?? '#EDF1F3';
}

export function taskStatusSlug(status: string | null | undefined): string {
  return normalizeTaskStatus(status || 'unknown').replace(/\s+/g, '-');
}

export function isCompletedTaskStatus(status: string | null | undefined): boolean {
  return normalizeTaskStatus(status) === 'completed';
}

export function memberDisplayName(member: TeamMember | null | undefined): string {
  if (!member) {
    return 'Unknown';
  }
  return `${member.firstName || ''} ${member.lastName || ''}`.trim()
    || member.email
    || 'Unknown';
}

export function priorityLabel(priority: string | null | undefined): string {
  const value = (priority || 'medium').trim();
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

export function priorityClass(priority: string | null | undefined): string {
  return `priority-${(priority || 'medium').toLowerCase()}`;
}
